import { useState, useEffect, useCallback } from "react";
import type { WalletClient } from "viem";
import { useWalletClient } from "wagmi";

type GasEstimationFunction = (walletClient: WalletClient) => Promise<bigint>;

interface UseSimpleGasEstimationProps {
    estimateGasFn: GasEstimationFunction;
}

export function useSimpleGasEstimation({ estimateGasFn }: UseSimpleGasEstimationProps) {
    const [gasEstimate, setGasEstimate] = useState<bigint | null>(null);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const { data: walletClient } = useWalletClient();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Can either memo this function or ignore this lint rule
    useEffect(() => {
        let isMounted = true;

        const estimate = async () => {
            if (!walletClient) return;

            setIsEstimatingGas(true);
            setError(null);

            try {
                const estimate = await estimateGasFn(walletClient);
                if (isMounted) {
                    setGasEstimate(estimate);
                }
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : "Failed to estimate gas";
                setError(new Error(errorMessage));
                if (isMounted) {
                    setIsEstimatingGas(false);
                }
            }

            return () => {
                isMounted = false;
            };
        };

        estimate();

        return () => {
            isMounted = false;
        };
    }, [walletClient]);

    const estimateGas = useCallback(() => {
        setGasEstimate(null);
        setError(null);
        setIsEstimatingGas(true);
    }, [setGasEstimate, setError, setIsEstimatingGas]);

    return {
        gasEstimate,
        isEstimatingGas,
        error,
        estimateGas,
    };
}
